import React from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 50px;
  left: 10px;
  right: 10px;
  
  /* Modern dark design */
  background: linear-gradient(135deg, rgba(25, 30, 50, 0.98) 0%, rgba(20, 25, 45, 0.98) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 12px;
  
  z-index: 1000;
  max-width: 240px;
  min-width: 200px;
  width: auto;
  
  /* Modern shadow */
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(99, 102, 241, 0.1);
  
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  
  display: flex;
  flex-direction: column;
  padding: 8px;
  
  animation: ${fadeIn} 0.2s ease-out;
  
  @media (max-width: 768px) {
    left: 5px;
    right: 5px;
    max-height: 250px;
  }
  
  /* Modern scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.5);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.7);
  }
`;

const RegionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  padding: 12px 12px 6px 12px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  margin-top: 8px;
  
  &:first-child {
    margin-top: 0;
  }
`;

const LanguageOption = styled.div`
  padding: 10px 12px;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  border-radius: 8px;
  font-weight: 500;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    color: #fff;
    transform: translateX(2px);
  }
  
  &:active {
    background: rgba(99, 102, 241, 0.3);
  }
`;

/**
 * Language selector dropdown component
 * Displays languages grouped by region
 */
const LanguageSelector = ({ 
  languages, 
  onSelect, 
  onMouseLeave 
}) => {
  // Group languages by region
  const groupedLanguages = languages.reduce((groups, lang) => {
    if (!groups[lang.region]) {
      groups[lang.region] = [];
    }
    groups[lang.region].push(lang);
    return groups;
  }, {});

  return (
    <DropdownContainer onMouseLeave={onMouseLeave}>
      {Object.entries(groupedLanguages).map(([region, langs]) => (
        <React.Fragment key={region}>
          <RegionTitle>{region.replace(/_/g, ' ')}</RegionTitle>
          {langs.map(lang => (
            <LanguageOption 
              key={lang.code} 
              onClick={() => onSelect(lang.code)}
            >
              {lang.name}
            </LanguageOption>
          ))}
        </React.Fragment>
      ))}
    </DropdownContainer>
  );
};

LanguageSelector.propTypes = {
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      region: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  onMouseLeave: PropTypes.func
};

LanguageSelector.defaultProps = {
  onMouseLeave: () => {}
};

export default LanguageSelector; 